import { List, Map, Set as ImmutableSet } from "immutable";
import { createSelector } from "reselect";
import i18n from "~/i18n";
import Project, { MEMBER_PERMISSIONS } from "~/records/record.project.jsx";
import { getAllCompanies } from "~/store/selectors/selector.companies";
import Angie from "~/utils/angie";
import { isMemberWithoutPermissions } from "~/utils/userHelper.js";
import { descSortCompare, sortCompare, calculatePercentForProgress } from "~/utils/utils.jsx";
import { getConfigOption, getRawPersonalizedConfigOption } from "./selector.configOptions.jsx";
import { getFavoriteProjectIds } from "./selector.favorites.jsx";
import { getByTypeAndId } from "./selector.general.jsx";
import { getAllProjectLabels } from "./selector.labels.jsx";
import { getProjectCategories } from "./selector.projectCategories.jsx";
import { getTaskListById } from "./selector.taskLists.jsx";

export const getAllProjects = (state) => state.projects;
export const getProjectsIdName = (state) => state.projects_id_name;

export const getProjectsIdNumberName = createSelector(getAllProjects, (projects) => {
  return projects.toArray().map((project) => {
    return {
      id: project.id,
      name: project.name,
      number: project.project_number,
    };
  });
});

export const getAllProjectsSortedByKey = createSelector(
  getAllProjects,
  (state, sortByKey) => sortByKey,
  (projects, sortByKey = `name`) => {
    return projects.sortBy((project) => {
      let sortBy = project.get(sortByKey);
      if (typeof sortBy === `string`) {
        sortBy = sortBy.toLowerCase();
      }
      return sortBy;
    });
  }
);

export const getProjectIdsSortedByKey = createSelector(
  (state, sortBy = "name") => getAllProjectsSortedByKey(state, sortBy),
  (projects) => projects.map((project) => project.id).toArray()
);

export const getActiveProjects = (state) => {
  return state.projects
    .filter((project) => !project.is_completed && !project.is_trashed)
    .sortBy((project) => project.name);
};

export const getHasActiveProjects = createSelector(getActiveProjects, (activeProjects) => {
  return activeProjects.size > 0;
});

export const getProjectsIdNameArray = createSelector(getActiveProjects, (projects) => {
  return projects.toArray().map((project) => {
    return {
      id: project.id,
      name: project.name,
    };
  });
});

export const getActiveUnsortedProjects = (state) =>
  state.projects.filter((project) => !project.is_completed && !project.is_trashed);

export const getActiveUnsortedProjectIdsWithoutSampleProjects = (state) =>
  state.projects
    .filter((project) => !project.is_completed && !project.is_trashed && !project.is_sample)
    .map((project) => project.id);

export const getCompletedProjects = (state) =>
  state.projects.filter((project) => project.is_completed && !project.is_trashed);

export const getFilteredClosedProjects = createSelector(
  getCompletedProjects,
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_client"),
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_label"),
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_category"),
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_leader"),
  (projects, filterByClient, filterByLabel, filterByCategory, filterByLead) => {
    return projects.toArray().reduce((acc, project) => {
      acc.push(project);

      if (
        (filterByClient && filterByClient.size > 0 && !filterByClient.includes(project.company_id)) ||
        (filterByLabel && filterByLabel.size > 0 && !filterByLabel.includes(project.label_id)) ||
        (filterByCategory && filterByCategory.size > 0 && !filterByCategory.includes(project.category_id)) ||
        (filterByLead && filterByLead.size > 0 && !filterByLead.includes(project.leader_id))
      ) {
        const index = acc.indexOf(project);
        acc.splice(index, 1);
      }

      return acc;
    }, []);
  }
);

export const getCompletedProjectsForList = createSelector(
  getFilteredClosedProjects,
  (state) => getRawPersonalizedConfigOption(state, "sort_mode_completed_projects"),
  (projects, sortBy) => {
    let sortingKey;
    switch (sortBy) {
      // case 'storage':
      //   sortingKey = 'storage...'
      //   break
      default:
        sortingKey = "completed_on";
    }

    return projects.sort((p1, p2) => descSortCompare(p1[sortingKey], p2[sortingKey]));
  }
);

export const getSampleProjectIds = (state) => {
  return state.projects
    ?.filter((project) => !project.is_completed && !project.is_trashed && project.is_sample)
    .map((project) => project.id)
    .toArray();
};

export const getActiveProjectsForSelectize = createSelector(
  getProjectsIdName,
  (state, projectId) => projectId,
  (projects, projectId) =>
    projects
      .filter((value, key) => parseInt(key) !== projectId)
      .map((value, key) => ({ label: value, value: parseInt(key) }))
      .toArray()
);

export const getProjectById = (state, id) => state.projects.get(`${id}`);

export const getProjectNameSelector = createSelector(getProjectById, (project) =>
  project ? project.get("name") : null
);

export const getProjectNameWithIdSelector = createSelector(
  getProjectById,
  (state) => getConfigOption(state, "show_project_id"),
  (state, id, forceWithoutNumber) => forceWithoutNumber,
  (project, showProjectId, forceWithoutNumber = false) => {
    const projectName = project?.name;
    const projectNumber = project?.project_number;
    let name = projectName || "";
    if (showProjectId && projectNumber && !forceWithoutNumber) {
      name = `#${projectNumber}: ${projectName}`;
    }
    return name;
  }
);

export const getActiveProjectById = (state, id) => getActiveProjects(state).get(`${id}`);

export const getProjectByTaskListId = (state, taskListId) => {
  const taskList = getTaskListById(state, taskListId);
  return getProjectById(state, taskList.project_id.toString()) || new Project({ id: taskList.project_id });
};

export const getProjectMembersById = createSelector(getProjectById, (project) => {
  if (!project?.members) {
    return [];
  }

  if (Array.isArray(project.members)) {
    return project.members;
  }

  if (List.isList(project.members)) {
    return project.members.toArray();
  }

  return [];
});

export const getProjectLeaderById = createSelector(getProjectById, (project) => project?.leader_id);

export const getIsProjectCompleted = createSelector(getProjectById, (project = Map()) =>
  project.get(`is_completed`, false)
);

const defaultProjectColor = `#63A7DE`;
const getProjectLabelId = createSelector(getProjectById, (project) => (project ? project.get(`label_id`, 0) : 0));

export const getProjectLabelColor = createSelector(getProjectLabelId, getAllProjectLabels, (labelId, projectLabels) =>
  labelId > 0 ? projectLabels.getIn([`${labelId}`, `color`]) : defaultProjectColor
);

export const getProjectLabel = createSelector(getProjectLabelId, getAllProjectLabels, (labelId, projectLabels) =>
  projectLabels.get(`${labelId}`)
);

export const getProjectIdsByUserId = createSelector(
  (state, userId, sortBy = "name") => getAllProjectsSortedByKey(state, sortBy),
  (state, userId) => userId,
  (projects, userId) =>
    projects
      .filter((project) => project.members.indexOf(userId) > -1)
      .map((project) => project.id)
      .toArray()
);

export const getProjectByTypeAndId = (state, type, id) => {
  const object = getByTypeAndId(state, type, id);

  if (!object) {
    return undefined;
  }

  if (object.class === "Project") {
    return object;
  }

  if (object.project_id) {
    return getProjectById(state, object.project_id);
  }

  return undefined;
};

export const getFilteredActiveProjects = createSelector(
  getActiveUnsortedProjects,
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_client"),
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_label"),
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_category"),
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_leader"),
  (projects, filterByClient, filterByLabel, filterByCategory, filterByLead) => {
    return projects.toArray().reduce((acc, project) => {
      acc.push(project);

      if (
        (filterByClient && filterByClient.size > 0 && !filterByClient.includes(project.company_id)) ||
        (filterByLabel && filterByLabel.size > 0 && !filterByLabel.includes(project.label_id)) ||
        (filterByCategory && filterByCategory.size > 0 && !filterByCategory.includes(project.category_id)) ||
        (filterByLead && filterByLead.size > 0 && !filterByLead.includes(project.leader_id))
      ) {
        const index = acc.indexOf(project);
        acc.splice(index, 1);
      }

      return acc;
    }, []);
  }
);

export const getActiveSortedProjects = createSelector(
  getFilteredActiveProjects,
  (state) => getRawPersonalizedConfigOption(state, "sort_mode_projects"),
  (state) => state.projectCompletedTasks,
  (state) => state.projectOpenTasks,
  (projects, sortBy, projectCompletedTasks, projectOpenTasks) => {
    let sortingKey;
    switch (sortBy) {
      case "activity":
        sortingKey = "last_activity_on";
        break;
      case "created":
        sortingKey = "created_on";
        break;
      case "progress":
        sortingKey = "progress";
        break;
      default:
        sortingKey = "name";
    }

    if (sortingKey === "progress") {
      return projects.sort((p1, p2) => {
        return (
          calculatePercentForProgress(projectOpenTasks.get(`${p2.id}`), projectCompletedTasks.get(`${p2.id}`)) -
          calculatePercentForProgress(projectOpenTasks.get(`${p1.id}`), projectCompletedTasks.get(`${p1.id}`))
        );
      });
    }

    return projects.sort((p1, p2) => {
      // @TODO Remove this logging after bug is catch
      if (p1[sortingKey] === undefined || p2[sortingKey] === undefined) {
        Angie.logger.error("Values for sorting projects is not defined");
      }

      return sortingKey === "name"
        ? p1[sortingKey]?.trim().localeCompare(p2[sortingKey]?.trim())
        : descSortCompare(p1[sortingKey], p2[sortingKey]);
    });
  }
);

export const getProjectsForList = createSelector(
  getActiveSortedProjects,
  (state) => getFavoriteProjectIds(state),
  getAllProjectLabels,
  (state) => state.companies,
  getProjectCategories,
  (state) => state.users,
  (state) => getRawPersonalizedConfigOption(state, "group_mode_projects"),
  (state) => getRawPersonalizedConfigOption(state, "sort_mode_project_groups"),
  (state) => state.logged_user?.id,
  (projects, favorites, labels, companies, categories, users, groupBy, sortByProjectGroups, loggedUserId) => {
    const grouped = projects.reduce((acc, project) => {
      const p = {
        id: project.id,
        star: favorites.includes(project.id) ? 2 : 1,
        label: project.label_id,
        client: project.company_id,
        category: project.category_id,
        lead: project.leader_id,
      };

      if (!p[groupBy]) {
        return acc[0] ? { ...acc, 0: [...acc[0], p.id] } : { ...acc, 0: [p.id] };
      }
      if (acc[p[groupBy]]) {
        acc[p[groupBy]].push(p.id);
        return acc;
      }
      return { ...acc, [p[groupBy]]: [p.id] };
    }, {});

    const getKeyName = (id) => {
      const groupId = parseInt(id);
      if (groupId === 0) return i18n.t("Other");

      let name;
      switch (groupBy) {
        case "label":
          name = labels.getIn([`${id}`, "name"], "");
          break;
        case "client":
          name = companies[`${id}`]?.name || "";
          break;
        case "category":
          name = categories[id] ? categories[id].name : "";
          break;
        case "lead":
          name = users.getIn([`${id}`, "display_name"], "");
          break;
        case "star":
          name = groupId === 1 ? i18n.t("Other") : i18n.t("Favorite");
          break;
      }

      return name;
    };

    const sortedKeys = Object.keys(grouped).sort((k1, k2) => {
      const key1 = parseInt(k1);
      const key2 = parseInt(k2);

      // sort by favorite, always only two groups
      if (groupBy === "star") return sortByProjectGroups !== "desc" ? descSortCompare(k1, k2) : sortCompare(k1, k2);

      // Other group always goes down
      if (key1 === 0 && key2 !== 0) {
        return 1;
      } else if (key1 !== 0 && key2 === 0) {
        return -1;
      }

      return sortByProjectGroups !== "desc"
        ? getKeyName(key1).localeCompare(getKeyName(key2))
        : -1 * getKeyName(key1).localeCompare(getKeyName(key2));
    });

    if (groupBy === "lead" && loggedUserId) {
      sortedKeys.sort((id1, id2) => {
        if (parseInt(id1) === loggedUserId) {
          return -1;
        }

        return parseInt(id2) === loggedUserId ? 1 : 0;
      });
    }

    return sortedKeys.reduce(
      (acc, key) => [
        ...acc,
        {
          groupId: parseInt(key),
          groupName: getKeyName(key),
          projects: grouped[key],
        },
      ],
      []
    );
  }
);

const resolveOptions = (projects, items, filteredBy, groupByKey, nameKey, noOptionName = "") => {
  let set = new ImmutableSet();
  const grouped = projects.groupBy((p) => p.get(groupByKey));
  grouped.keySeq().forEach((id) => (set = set.add(id)));
  filteredBy.forEach((id) => (set = set.add(id)));

  const data = [];

  const isMap = Map.isMap(items);
  set.forEach((id) => {
    const item = isMap ? items.get(`${id}`) : items[id];
    if (item) {
      const name = isMap ? item.get(nameKey) : item[nameKey];
      data.push({ id, name: name });
    }
  });

  let result = [...data.sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()))];

  if (noOptionName && set.includes(0)) {
    result = [{ id: 0, name: noOptionName }, ...result];
  }

  return result;
};

const resolveOptionsForCompletedProjects = (ids, items, filteredBy, displayName, noOptionName = "") => {
  let result;
  const set = new Set([...ids, ...filteredBy]);
  const isMap = Map.isMap(items);
  result = Array.from(set)
    .reduce((prev, id) => {
      let name;
      if (isMap) {
        name = items.getIn([`${id}`, displayName]);
      } else {
        name = items[id] && items[id][displayName];
      }
      if (name) {
        prev.push({ id, name: name });
      }
      return prev;
    }, [])
    .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));

  if (noOptionName && set.has(0)) {
    result = [{ id: 0, name: noOptionName }, ...result];
  }

  return result;
};

export const getCategoriesForActiveProjects = createSelector(
  getActiveUnsortedProjects,
  getProjectCategories,
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_category"),
  (projects, categories, filteredBy) =>
    resolveOptions(projects, categories, filteredBy, "category_id", "name", i18n.t("No category"))
);

export const getCategoriesForCompletedProjects = createSelector(
  (state, ids) => ids,
  getProjectCategories,
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_category"),
  (ids, categories, filteredBy) =>
    resolveOptionsForCompletedProjects(ids, categories, filteredBy, "name", i18n.t("No category"))
);

export const getClientsForActiveProjects = createSelector(
  getActiveUnsortedProjects,
  (state) => state.companies,
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_client"),
  (projects, clients, filteredBy) => resolveOptions(projects, clients, filteredBy, "company_id", "name", "")
);

export const getClientsForCompletedProjects = createSelector(
  (state, ids) => ids,
  (state) => state.companies,
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_client"),
  (ids, clients, filteredBy) => resolveOptionsForCompletedProjects(ids, clients, filteredBy, "name")
);

export const getLabelsForActiveProjects = createSelector(
  getActiveUnsortedProjects,
  getAllProjectLabels,
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_label"),
  (projects, labels, filteredBy) => resolveOptions(projects, labels, filteredBy, "label_id", "name", i18n.t("No label"))
);

export const getLabelsForCompletedProjects = createSelector(
  (state, ids) => ids,
  getAllProjectLabels,
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_label"),
  (ids, labels, filteredBy) => resolveOptionsForCompletedProjects(ids, labels, filteredBy, "name", i18n.t("No label"))
);

export const getLeadersForActiveProjects = createSelector(
  getActiveUnsortedProjects,
  (state) => state.users,
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_leader"),
  (projects, users, filteredBy) =>
    resolveOptions(projects, users, filteredBy, "leader_id", "display_name", i18n.t("No leader"))
);

export const getLeadersForCompletedProjects = createSelector(
  (state, ids) => ids,
  (state) => state.users,
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_leader"),
  (ids, users, filteredBy) =>
    resolveOptionsForCompletedProjects(ids, users, filteredBy, "display_name", i18n.t("No leader"))
);

export const hasOptionsForActiveProjectsFilter = createSelector(
  getCategoriesForActiveProjects,
  getLeadersForActiveProjects,
  getClientsForActiveProjects,
  getLabelsForActiveProjects,
  (categories, leaders, companies, labels) =>
    categories.length > 1 || leaders.length > 1 || companies.length > 1 || labels.length > 1
);

export const canSeeTrackingObject = createSelector(
  (state) => state.logged_user,
  getProjectById,
  (loggedUser, currentProject) => {
    if (!loggedUser || !currentProject) return false;
    if (!currentProject.is_tracking_enabled) return false; // if tracking is not enabled, no one can see it
    if (loggedUser.isOwner() || loggedUser.id === currentProject.leader_id) return true; // project leaders and owners can see tracking objects
    if (!currentProject.members.includes(loggedUser.id)) return false; // users outside this project can't add tracking objects
    if (loggedUser.isClient() && currentProject.is_client_reporting_enabled) return true; // clients can only see the objects if client reporting is enabled
    return !loggedUser.isClient(); // everyone except client can see tracking objects
  }
);

export const getProjectOpenTasksNumber = (state, id) => {
  return state.projectOpenTasks.get(`${id}`, undefined);
};

export const getProjectCompletedTasksNumber = (state, id) => {
  return state.projectCompletedTasks.get(`${id}`, undefined);
};

export const searchActiveProjectsByName = createSelector(
  getActiveProjects,
  (state, searchBy) => searchBy,
  (state) => getFavoriteProjectIds(state),
  (activeProjects, searchBy, favoriteProjectIds) => {
    if (!searchBy) {
      return [];
    }

    return activeProjects
      .filter((p) => {
        return p.get("name").toLowerCase().includes(searchBy.toLowerCase());
      })
      .toList()
      .toJS()
      .map((p) => {
        return {
          id: p.id,
          name: p.name,
          url_path: p.url_path,
          is_starred: favoriteProjectIds.includes(p.id),
        };
      })
      .sort((p1, p2) => {
        return sortCompare(p1.is_starred ? 1 : 2, p2.is_starred ? 1 : 2);
      });
  }
);

export const getProjectsForPicker = createSelector(
  getActiveProjects,
  getAllProjectLabels,
  (state) => getAllCompanies(state),
  (state) => state.users,
  (state) => getProjectCategories(state),
  (state) => getFavoriteProjectIds(state),
  (state, groupBy) => groupBy,
  (state, groupBy, tracking_enabled) => tracking_enabled,
  (state, groupBy, tracking_enabled, projectsListIds) => projectsListIds,
  (state, groupBy, tracking_enabled, projectsListIds, filterStrictProjects) => filterStrictProjects,
  (state) => state.logged_user,
  (
    activeProjects,
    labels,
    companies,
    users,
    categories,
    favorites,
    groupBy = "favorites",
    tracking_enabled = undefined,
    projectsListIds = undefined,
    filterStrictProjects = false,
    loggedUser
  ) => {
    const config = {
      labels: {
        field: "label_id",
        objects: labels?.toJS() || [],
        other: i18n.t("Without Label"),
      },
      companies: {
        field: "company_id",
        objects: companies,
        other: i18n.t("Without Client"),
      },
      categories: {
        field: "category_id",
        objects: categories || [],
        other: i18n.t("Without Category"),
      },
      leaders: {
        field: "leader_id",
        objects: users?.toJS() || [],
        other: i18n.t("Without Leader"),
      },
      favorites: { objects: favorites, other: i18n.t("Other") },
    };

    if (config[groupBy] === undefined) {
      groupBy = "favorites";
    }

    if (isMemberWithoutPermissions(loggedUser) && filterStrictProjects) {
      activeProjects = activeProjects.filter(
        (project) =>
          project.member_permissions === MEMBER_PERMISSIONS.COLLABORATIVE || loggedUser.id === project.leader_id
      );
    }

    if (tracking_enabled !== undefined) {
      activeProjects = activeProjects.filter((project) => project.is_tracking_enabled === Boolean(tracking_enabled));
      if (projectsListIds) {
        activeProjects = activeProjects.filter((project) => projectsListIds.includes(project.id));
      }
    }

    let groups = activeProjects.reduce((acc, item) => {
      let group = item[config[groupBy].field] || 0;

      if (groupBy === "favorites" && favorites.includes(item.id)) {
        group = "favorites";
      }

      acc[group] = [...(acc[group] || []), { id: item.id, name: item.name }];
      return acc;
    }, {});

    groups = Object.entries(groups)
      .map(([id, options]) => {
        options.sort((a, b) => a.name.localeCompare(b.name));

        let name = config[groupBy].objects[id]?.name;

        if (groupBy === "leaders") {
          name = config[groupBy].objects[id]?.display_name;
        }

        name = name || config[groupBy].other;

        if (id === "favorites") {
          name = i18n.t("Favorite");
        }

        return { id: config[groupBy].objects[id]?.id || id, name, options };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    const index = groups.findIndex((item) => item.id === 0);

    if (index !== -1) groups.push(groups.splice(index, 1)[0]);

    return groupBy === "favorites" && groups.length === 1 ? groups[0].options : groups;
  }
);
export const getProjectBasedOnEstimate = createSelector(
  getAllProjects,
  (_, estimateId) => estimateId,
  (projects, estimateId) => {
    return projects
      ?.toList()
      .toJS()
      .filter(
        (project) => !project.is_trashed && project.based_on_type === "Estimate" && project.based_on_id === estimateId
      )
      .map((i) => ({ id: i.id, name: i.name }));
  }
);

export const getProjectsByIds = createSelector(
  getAllProjects,
  (_state, ids) => ids,
  (projects, ids) => {
    if (ids && Array.isArray(ids)) {
      return ids
        .map((id) => {
          if (projects.get(`${id}`)) {
            return { id, name: projects.get(`${id}`).name };
          }
        })
        .filter(Boolean)
        .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));
    }
    return [];
  }
);

export const getProjectsByCompanyId = createSelector(
  getAllProjects,
  (state, companyId) => companyId,
  (projects, companyId) => {
    return projects
      ?.toList()
      .toJS()
      .filter((project) => {
        return !project.is_trashed && project.company_id === companyId;
      })
      .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));
  }
);
